import React, { useCallback, useEffect, useState, useContext } from 'react'
import { User, Product, Campaign } from 'app/types/authorization'
import { CheckoutResponse } from 'app/types/checkout'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken } from 'app/utils/cognito'
import { CompanyData, CompanyProfile } from 'app/types/organization'
import LivedayLoader from '../components/old/animations/LivedayLoader'
import { AuthContext } from 'app/utils/context'
import { captureException } from '@sentry/nextjs'
import { CheckoutForm } from './Checkout'
import { SurfboardCheckoutCart } from './SurfboardCheckoutCart'

// Props for the CheckoutCart component
interface CartProps {
  user: User | undefined
  eventSk: string
  organization: string
  items: Product[]
  campaigns: Campaign[]
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  companyProfile: CompanyProfile | null
  companyData: CompanyData[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => Promise<void>
  name?: string
  email?: string
  phoneNumber?: string
  poolId?: string
  userPoolId?: string
  shippingMethod?: 'pickup' | 'shipping'
}

// Component to handle the checkout process
export function CheckoutCart({ organization, ...props }: CartProps) {
  if (organization === '2pTWvRLrXsY3RdHYa7fwamWVE8e') {
    return <InnerCheckoutCart organization={organization} {...props} />
  }
  if (organization === '2vjYW5qER9OnYBi0Ds6U3bqeh2l') {
    return <InnerCheckoutCart organization={organization} {...props} />
  }
  if (organization === '2sw85TBpuVDX9wNg57mz760lE3N') {
    return <InnerCheckoutCart organization={organization} {...props} />
  }
  return <SurfboardCheckoutCart organization={organization} {...props} />
}

// Component to handle the checkout process
function InnerCheckoutCart({
  user,
  eventSk,
  organization,
  setShowSuccessMessage,
  setShowFailureMessage,
  companyProfile,
  companyData,
  setCurrentStep,
  refetchCart,
  name,
  email,
  phoneNumber,
  userPoolId,
  poolId,
  shippingMethod,
}: CartProps) {
  const { state } = useContext(AuthContext)
  const [checkoutSession, setCheckoutSession] = useState<
    CheckoutResponse | undefined
  >(undefined)

  // Function to fetch checkout session data
  const fetchCheckoutSession = useCallback(async () => {
    try {
      const idToken = await clientGetIdToken()
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      if (poolId && userPoolId === undefined) {
        headers['Company-Pool-Token'] = poolId
      }
      if (userPoolId && poolId) {
        headers['Public-Pool-Token'] = poolId
        headers['Public-Pool-User-Token'] = userPoolId
      }
      let body: undefined | { shipping_method: string } = undefined
      if (shippingMethod) {
        body = {
          shipping_method: shippingMethod,
        }
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing_session`,
        {
          method: 'PUT',
          headers,
          body: body ? JSON.stringify(body) : undefined,
        }
      )

      const checkoutSessionData: StandardAPIRequest<CheckoutResponse> =
        await response.json()
      if (checkoutSessionData.error) {
        throw new Error(checkoutSessionData.error.message)
      }
      setCheckoutSession(checkoutSessionData.data ?? undefined)
    } catch (err) {
      captureException(err)
    }
  }, [eventSk, state.cartToken, poolId, userPoolId, shippingMethod])

  // Fetch checkout session data on component mount
  useEffect(() => {
    fetchCheckoutSession()
  }, [fetchCheckoutSession])

  // Show loader while fetching checkout session data
  if (checkoutSession === undefined) {
    return <LoadingComponent />
  }

  // Render checkout form with fetched data
  return (
    <CheckoutFormWrapper
      user={user}
      checkoutSession={checkoutSession}
      eventSk={eventSk}
      setShowSuccessMessage={setShowSuccessMessage}
      setShowFailureMessage={setShowFailureMessage}
      items={checkoutSession.checkout_session.cart.items}
      campaigns={checkoutSession.checkout_session.cart.campaigns ?? []}
      companyProfile={companyProfile}
      companyData={companyData}
      organization={organization}
      setCheckoutSession={setCheckoutSession}
      setCurrentStep={setCurrentStep}
      refetchCart={refetchCart}
      name={name}
      email={email}
      phoneNumber={phoneNumber}
      shippingMethod={shippingMethod}
    />
  )
}

// Loading component
const LoadingComponent: React.FC = () => {
  return <LivedayLoader />
}

// Wrapper component for CheckoutForm
const CheckoutFormWrapper: React.FC<
  CartProps & {
    checkoutSession: CheckoutResponse
    setCheckoutSession: React.Dispatch<
      React.SetStateAction<CheckoutResponse | undefined>
    >
  }
> = ({
  user,
  checkoutSession,
  eventSk,
  setShowSuccessMessage,
  setShowFailureMessage,
  setCheckoutSession,
  items,
  campaigns,
  companyProfile,
  companyData,
  organization,
  setCurrentStep,
  refetchCart,
  name,
  email,
  phoneNumber,
  poolId,
  shippingMethod,
}) => {
  return (
    <CheckoutForm
      user={user}
      checkoutSession={checkoutSession}
      eventSk={eventSk}
      setShowSuccessMessage={setShowSuccessMessage}
      setShowFailureMessage={setShowFailureMessage}
      setCheckoutSession={setCheckoutSession}
      items={items}
      campaigns={campaigns}
      companyProfile={companyProfile}
      companyData={companyData}
      organization={organization}
      setCurrentStep={setCurrentStep}
      refetchCart={refetchCart}
      name={Array.isArray(name) ? name[0] : name}
      email={Array.isArray(email) ? email[0] : email}
      phoneNumber={Array.isArray(phoneNumber) ? phoneNumber[0] : phoneNumber}
      poolId={poolId}
      shippingMethod={shippingMethod}
    />
  )
}
